// raw-list-schemas.ts — the registry of raw-row list schemas.
//
// A raw-row list endpoint is a permission gate + one SELECT: it ships `{schema, ...raw rows}` and no columns, cells or HTML. The
// adopter registered here for that schema turns the rows into the catalog table's {columns, rows}; render-table.ts asks once per
// fetch. A new list adds one entry (its own *-rows.ts module) and never edits render-table.ts.

import type { CatalogTableResponse } from './types';
import { adoptEncounterList, ENCOUNTER_LIST_SCHEMA, type EncounterListResponse } from './encounter-list-rows';
import { adoptPlayerCharacterList, PLAYER_CHARACTER_LIST_SCHEMA, type PlayerCharacterListResponse } from './player-character-list-rows';

import { prepareDefinitionList, DEFINITION_LIST_SCHEMA, type DefinitionListResponse } from './definition-list-rows';

type RawListAdopter = (data: unknown, root?: HTMLElement, endpoint?: string) => CatalogTableResponse | Promise<CatalogTableResponse>;

const ADOPTERS: Readonly<Record<string, RawListAdopter>> = {
    [DEFINITION_LIST_SCHEMA]: (data, root, endpoint) => prepareDefinitionList(data as DefinitionListResponse, {
        withIcon: root?.dataset.ftDefinitionWithIcon === '1',
        openInfo: root?.dataset.ftDefinitionOpenInfo === '1',
        hideDescription: root?.dataset.ftDefinitionHideDescription === '1',
        showEnvironment: root?.dataset.ftDefinitionShowEnvironment === '1',
    }, endpoint),
    [ENCOUNTER_LIST_SCHEMA]: data => adoptEncounterList(data as EncounterListResponse),
    [PLAYER_CHARACTER_LIST_SCHEMA]: data => adoptPlayerCharacterList(data as PlayerCharacterListResponse),
};

/** The adopter for a response's `schema`, or null when the response is already a {columns, rows} table. */
export function rawListAdopter(schema: unknown): RawListAdopter | null {
    return (typeof schema === 'string' && Object.prototype.hasOwnProperty.call(ADOPTERS, schema) ? ADOPTERS[schema] : undefined) ?? null;
}
